import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  MessageSquare, Send, ArrowLeft, User, Car, UtensilsCrossed,
  Bike, Clock, RefreshCw, Receipt, Star, FileText, Download
} from 'lucide-react';
import { messagesAPI, restaurantAPI, driverAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const ServiceMessaging = ({ openConversationId, openConversationData, onConversationOpened }) => {
  const { getAuthToken, user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [orderClosed, setOrderClosed] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showRiderReview, setShowRiderReview] = useState(false);
  const [riderReviewRating, setRiderReviewRating] = useState(5);
  const [riderReviewComment, setRiderReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const pollingRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversations(token, 'guest');
      // Filter to service-related conversations only
      const serviceConvs = data.filter(c =>
        c.conversationType === 'service' ||
        c.conversationType === 'delivery' ||
        c.conversationType === 'delivery_dropoff' ||
        c.conversationType === 'ride' ||
        c.conversationType === 'restaurant' ||
        c.conversationType === 'delivery_pickup'
      );
      setConversations(serviceConvs);
      return serviceConvs;
    } catch (err) {
      console.error('Error fetching service conversations:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Auto-open conversation when openConversationId is provided (from "Message Restaurant" button)
  useEffect(() => {
    if (!openConversationId) return;

    const autoOpenConversation = async () => {
      // If we have conversation data passed directly, use it
      if (openConversationData && openConversationData._id === openConversationId) {
        setSelectedConversation(openConversationData);
        if (onConversationOpened) onConversationOpened();
        return;
      }

      // Otherwise, try to find it in the conversations list
      const existingConv = conversations.find(c => c._id === openConversationId);
      if (existingConv) {
        setSelectedConversation(existingConv);
        if (onConversationOpened) onConversationOpened();
        return;
      }

      // If not found, fetch conversations again and look for it
      const freshConvs = await fetchConversations();
      const foundConv = freshConvs.find(c => c._id === openConversationId);
      if (foundConv) {
        setSelectedConversation(foundConv);
      } else {
        // Last resort — fetch the conversation directly by ID
        try {
          const token = getAuthToken();
          const data = await messagesAPI.getConversation(openConversationId, token);
          if (data) {
            setSelectedConversation(data.conversation || data);
          }
        } catch (err) {
          console.error('Could not auto-open conversation:', err);
        }
      }
      if (onConversationOpened) onConversationOpened();
    };

    autoOpenConversation();
  }, [openConversationId, openConversationData, onConversationOpened, conversations, fetchConversations, getAuthToken]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversation(conversationId, token);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setMessages([]);
    }
  }, [getAuthToken]);

  useEffect(() => {
    if (selectedConversation?._id) {
      fetchMessages(selectedConversation._id);
      // Check if related food order is closed (delivered/cancelled)
      if (selectedConversation.relatedType === 'food_order' && selectedConversation.relatedId) {
        const token = getAuthToken();
        restaurantAPI.getMyOrder(selectedConversation.relatedId, token)
          .then(order => {
            setOrderClosed(['delivered', 'cancelled', 'rejected'].includes(order?.status));
          })
          .catch(() => setOrderClosed(false));
      } else if (selectedConversation.conversationType === 'restaurant') {
        setOrderClosed(false); // No linked order — allow messaging
      } else {
        setOrderClosed(false);
      }

      // Auto-focus the message input when opening a conversation
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 300);
    }
  }, [selectedConversation, fetchMessages, getAuthToken]);

  // Instant messaging — poll for new messages every 3 seconds when a conversation is open
  useEffect(() => {
    if (!selectedConversation?._id) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Poll for new messages
    pollingRef.current = setInterval(() => {
      fetchMessages(selectedConversation._id);
    }, 3000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [selectedConversation?._id, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    setSending(true);
    try {
      const token = getAuthToken();
      const message = await messagesAPI.sendMessage({
        conversationId: selectedConversation._id,
        content: newMessage.trim()
      }, token);
      setMessages(prev => [...prev, message]);
      setNewMessage('');
      // Re-focus input after sending
      setTimeout(() => messageInputRef.current?.focus(), 100);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleRiderReview = async () => {
    if (!selectedConversation) return;
    setSubmittingReview(true);
    try {
      const token = getAuthToken();
      // Send review as a message with special type
      await messagesAPI.sendMessage({
        conversationId: selectedConversation._id,
        content: `⭐ Rider Review: ${riderReviewRating}/5 — ${riderReviewComment || 'No comment'}`
      }, token);
      toast({ title: 'Review Submitted', description: 'Thank you for reviewing the delivery rider!' });
      setShowRiderReview(false);
      setRiderReviewRating(5);
      setRiderReviewComment('');
      fetchMessages(selectedConversation._id);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to submit review', variant: 'destructive' });
    } finally {
      setSubmittingReview(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const getServiceIcon = (conv) => {
    const type = conv.conversationType || '';
    if (type === 'ride') return <Car size={18} />;
    if (type === 'delivery') return <Bike size={18} />;
    if (type === 'delivery_pickup') return <Bike size={18} />;
    if (type === 'restaurant') return <UtensilsCrossed size={18} />;
    return <UtensilsCrossed size={18} />;
  };

  const getServiceColor = (conv) => {
    const type = conv.conversationType || '';
    if (type === 'ride') return 'bg-blue-500';
    if (type === 'delivery') return 'bg-orange-500';
    if (type === 'delivery_pickup') return 'bg-purple-500';
    if (type === 'restaurant') return 'bg-emerald-500';
    return 'bg-emerald-500';
  };

  const getServiceLabel = (conv) => {
    const type = conv.conversationType || '';
    if (type === 'ride') return '🚕 Taxi';
    if (type === 'delivery') return '🛵 Delivery Rider';
    if (type === 'delivery_pickup') return '🛵 Delivery Pickup';
    if (type === 'restaurant') return '🍴 Restaurant';
    if (type === 'service') return '📋 Service';
    return '📋 Order';
  };

  const getServiceBadgeColor = (conv) => {
    const type = conv.conversationType || '';
    if (type === 'ride') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (type === 'delivery') return 'bg-orange-100 text-orange-700 border-orange-200';
    if (type === 'delivery_dropoff') return 'bg-orange-100 text-orange-700 border-orange-200';
    if (type === 'delivery_pickup') return 'bg-purple-100 text-purple-700 border-purple-200';
    if (type === 'restaurant') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading && !selectedConversation) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  // No conversations yet — empty state
  if (conversations.length === 0 && !selectedConversation) {
    return (
      <div className="text-center py-12 text-gray-500">
        <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
        <p className="font-medium text-lg">No service messages</p>
        <p className="text-sm mt-1">
          Messages from restaurants, delivery riders, and taxi drivers will appear here
        </p>
        <div className="flex justify-center gap-4 mt-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Restaurants</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span> Delivery Riders</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Taxi Drivers</span>
        </div>
      </div>
    );
  }

  // Receipt modal
  if (showReceiptModal && receiptData) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-[#1F3A68] flex items-center gap-2">
            <Receipt size={18} /> Receipt
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowReceiptModal(false)}>
            Close ✕
          </Button>
        </div>
        <div className="p-6">
          <div className="text-center mb-4 pb-4 border-b border-dashed">
            <h4 className="font-bold text-lg text-[#1F3A68]">{receiptData.restaurantName || receiptData.serviceName || 'Service Receipt'}</h4>
            <p className="text-xs text-gray-500 mt-1">Order #{receiptData.orderNumber || receiptData.rideNumber || '—'}</p>
            <p className="text-xs text-gray-400">{receiptData.date ? new Date(receiptData.date).toLocaleString() : '—'}</p>
          </div>
          {receiptData.items?.map((item, i) => (
            <div key={i} className="flex justify-between text-sm py-1">
              <span>{item.quantity}× {item.name}</span>
              <span>£{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="border-t border-dashed mt-3 pt-3 space-y-1 text-sm">
            {receiptData.subtotal != null && (
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>£{receiptData.subtotal?.toFixed(2)}</span></div>
            )}
            {receiptData.deliveryFee != null && receiptData.deliveryFee > 0 && (
              <div className="flex justify-between text-gray-600"><span>Delivery Fee</span><span>£{receiptData.deliveryFee?.toFixed(2)}</span></div>
            )}
            {receiptData.vat != null && (
              <div className="flex justify-between text-gray-600"><span>VAT</span><span>£{receiptData.vat?.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold text-base pt-2 border-t">
              <span>Total</span><span>£{receiptData.total?.toFixed(2)}</span>
            </div>
          </div>
          {receiptData.deliveryAddress && (
            <p className="text-xs text-gray-500 mt-4">📍 {receiptData.deliveryAddress}</p>
          )}
          {receiptData.riderName && (
            <p className="text-xs text-gray-500 mt-1">🛵 Delivered by: {receiptData.riderName}</p>
          )}
        </div>
      </div>
    );
  }

  // Rider review modal
  if (showRiderReview) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-[#1F3A68] flex items-center gap-2">
            <Star size={18} /> Rate Delivery Rider
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowRiderReview(false)}>
            Close ✕
          </Button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 text-center">How was your delivery experience?</p>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                onClick={() => setRiderReviewRating(star)}
                className="text-3xl transition-transform hover:scale-110"
              >
                {star <= riderReviewRating ? '⭐' : '☆'}
              </button>
            ))}
          </div>
          <p className="text-center text-sm font-medium text-gray-700">
            {riderReviewRating === 5 ? 'Excellent!' : riderReviewRating === 4 ? 'Good' : riderReviewRating === 3 ? 'Average' : riderReviewRating === 2 ? 'Poor' : 'Very Poor'}
          </p>
          <Textarea
            placeholder="Leave a comment about the delivery rider (optional)..."
            value={riderReviewComment}
            onChange={e => setRiderReviewComment(e.target.value)}
            rows={3}
          />
          <p className="text-xs text-gray-500">
            Your review helps other customers make informed choices and encourages good service.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowRiderReview(false)} className="flex-1">Cancel</Button>
            <Button
              onClick={handleRiderReview}
              disabled={submittingReview}
              className="flex-1 bg-[#1F3A68]"
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Chat view
  if (selectedConversation) {
    const isDeliveryThread = selectedConversation.conversationType === 'delivery';
    return (
      <div className="flex flex-col h-[500px] border rounded-lg overflow-hidden">
        {/* Chat Header */}
        <div className="p-3 border-b bg-gray-50 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => {
            setSelectedConversation(null);
            setMessages([]);
            fetchConversations(); // Refresh list when going back
          }}>
            <ArrowLeft size={20} />
          </Button>
          <div className={`w-10 h-10 ${getServiceColor(selectedConversation)} rounded-full flex items-center justify-center text-white`}>
            {getServiceIcon(selectedConversation)}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-[#1F3A68]">{selectedConversation.hostName || selectedConversation.guestName || 'Service'}</h4>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getServiceBadgeColor(selectedConversation)}`}>
                {getServiceLabel(selectedConversation)}
              </span>
              {selectedConversation.relatedId && (
                <span className="truncate text-gray-400">Order #{selectedConversation.relatedId?.slice(-6)}</span>
              )}
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="View receipt"
              onClick={() => {
                setReceiptData(selectedConversation.receiptData || {
                  serviceName: selectedConversation.hostName || 'Service',
                  orderNumber: selectedConversation.orderNumber || selectedConversation.rideNumber,
                  date: selectedConversation.lastMessageAt,
                  total: selectedConversation.total,
                  items: selectedConversation.items || [],
                  riderName: selectedConversation.riderName
                });
                setShowReceiptModal(true);
              }}
            >
              <Receipt size={18} className="text-gray-500" />
            </Button>
            {isDeliveryThread && (
              <Button
                variant="ghost"
                size="icon"
                title="Rate delivery rider"
                onClick={() => setShowRiderReview(true)}
              >
                <Star size={18} className="text-amber-500" />
              </Button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Start the conversation by typing a message below</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isOwn = msg.senderId === user?.id;
              const showDate = index === 0 ||
                formatDate(msg.createdAt) !== formatDate(messages[index - 1]?.createdAt);

              return (
                <React.Fragment key={msg._id || index}>
                  {showDate && (
                    <div className="text-center">
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {formatDate(msg.createdAt)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${
                      isOwn
                        ? 'bg-[#1F3A68] text-white rounded-l-lg rounded-tr-lg'
                        : 'bg-gray-100 text-gray-900 rounded-r-lg rounded-tl-lg'
                    } px-4 py-2`}>
                      {!isOwn && (
                        <p className={`text-[10px] font-semibold mb-0.5 ${isOwn ? 'text-blue-200' : 'text-gray-500'}`}>
                          {getServiceLabel(selectedConversation)}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                      <div className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                        {formatTime(msg.createdAt)}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {selectedConversation?.lockedAt ? (
          <div className="p-3 border-t bg-amber-50 text-center">
            <p className="text-sm text-amber-700 font-medium">🔒 This conversation was locked after delivery completion.</p>
            <p className="text-xs text-amber-600 mt-1">Messages are view-only for record-keeping purposes.</p>
          </div>
        ) : orderClosed && selectedConversation?.relatedType === 'food_order' ? (
          <div className="p-3 border-t bg-gray-50 text-center">
            <p className="text-sm text-gray-500">This order is complete. Messaging is no longer available.</p>
            <p className="text-xs text-gray-400 mt-1">If you have an issue, use the "Report an Issue" button in the Food tab.</p>
          </div>
        ) : (
          <div className="p-3 border-t bg-gray-50 flex gap-2 items-end">
            <Textarea
              ref={messageInputRef}
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              className="flex-1 min-h-[40px] max-h-[100px] resize-none"
              rows={1}
              autoFocus
            />
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="bg-[#1F3A68] h-10"
            >
              <Send size={20} />
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Conversation List
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Service Messages</h3>
        <Button variant="ghost" size="sm" onClick={fetchConversations}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Legend */}
      <div className="flex gap-3 mb-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Restaurant</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span> Delivery Rider</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Taxi</span>
      </div>

      {conversations.map(conv => (
        <div
          key={conv._id}
          onClick={() => setSelectedConversation(conv)}
          className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <div className={`w-10 h-10 ${getServiceColor(conv)} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
            {getServiceIcon(conv)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{conv.hostName || conv.guestName || 'Service'}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getServiceBadgeColor(conv)}`}>
                  {getServiceLabel(conv)}
                </span>
              </div>
              <span className="text-xs text-gray-400">{formatDate(conv.lastMessageAt)}</span>
            </div>
            {conv.lastMessage && (
              <p className="text-xs text-gray-500 truncate mt-0.5">{conv.lastMessage}</p>
            )}
          </div>
          {conv.unreadGuest > 0 && (
            <Badge variant="destructive" className="bg-[#CB3B3B]">{conv.unreadGuest}</Badge>
          )}
        </div>
      ))}
    </div>
  );
};

export default ServiceMessaging;
